import { Footer } from './Footer';

export function Terms() {
  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-900 via-black to-violet-900 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="backdrop-blur-xl rounded-md bg-white/10 p-8 shadow-2xl border border-white/20 mb-8">
          <h1 className="text-4xl font-bold text-white mb-8 text-center">Terms of Service</h1>

          <div className="text-white/90 space-y-6 text-sm leading-relaxed">
            <p className="text-white/70">Last updated: July 31, 2025</p>

            <p>
              Welcome to The Sentience Company (&quot;<strong>Sentience</strong>,&quot; &quot;<strong>we</strong>,&quot; &quot;<strong>our</strong>&quot;).
              These Terms of Service (&quot;<strong>Terms</strong>&quot;) govern your access to and use of our website
              (sentience.company), applications, APIs, SDKs, and related services (collectively, the &quot;<strong>Service</strong>&quot;).
              By using the Service, you agree to these Terms. If you don’t agree, don’t use the Service.
            </p>

            <div className="rounded-md border border-white/20 bg-white/5 p-4">
              <p className="font-semibold text-white">ARBITRATION NOTICE &amp; CLASS ACTION WAIVER</p>
              <p className="mt-2">
                Unless you opt out within 30 days as described in Section 15, disputes will be resolved by binding,
                individual arbitration—not in court or as a class action.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">1) Acceptance</h2>
            <p>
              If you use the Service on behalf of an organization, you represent that you’re authorized to bind that
              organization, and “you” includes that organization.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">2) Who May Use</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>You must be old enough to consent to data sharing without a guardian under your local law (13+ in the U.S., 16+ in much of the EU).</li>
              <li>You must comply with these Terms and applicable laws.</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">3) Changes</h2>
            <p>
              We may update the Service or these Terms at any time by posting the revised Terms. Continued use means you accept the changes.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">4) Accounts</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Keep your credentials confidential and secure.</li>
              <li>You’re responsible for all activity under your account.</li>
              <li>Provide accurate information and keep it current.</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">5) Acceptable Use</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Don’t break the law or violate others’ rights.</li>
              <li>Don’t reverse-engineer, decompile, or hack the Service.</li>
              <li>Don’t copy, resell, lease, or sublicense the Service.</li>
              <li>Don’t disrupt, overload, or circumvent any part of the Service.</li>
              <li>Don’t use the Service to build or market a competing product.</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">6) Ownership &amp; License</h2>
            <p>
              Sentience owns the Service and associated IP. You own content you submit or upload (&quot;<strong>User Content</strong>&quot;).
              You grant Sentience a worldwide, royalty-free license to host, store, process, and display User Content
              as needed to operate and improve the Service.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">7) Feedback</h2>
            <p>
              If you send us feedback, you allow us to use it without restriction to improve the Service, without any obligation to you.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">8) Subscriptions &amp; Fees</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Some features are paid. Prices and plan details are on our site and may change.</li>
              <li>Fees are in U.S. Dollars, renew automatically, and are generally non-refundable.</li>
              <li>You can cancel anytime; access continues until the current billing period ends.</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">9) Privacy</h2>
            <p>
              Our Privacy Policy explains how we collect and use data. By using the Service, you agree to it.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">10) Confidentiality</h2>
            <p>
              If we share non-public information with you, keep it confidential and use it only for interacting with the Service or as otherwise permitted in writing.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">11) Disclaimers</h2>
            <p>
              The Service is provided <strong>&quot;as is&quot;</strong> and <strong>&quot;as available&quot;</strong> without warranties of any kind, express or implied,
              including merchantability, fitness for a particular purpose, non-infringement, availability, or accuracy.
              We don’t guarantee error-free or uninterrupted operation or data preservation.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">12) Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Sentience isn’t liable for indirect, incidental, special, consequential,
              or punitive damages, or lost profits, revenues, or data. Our total liability for all claims in any 12-month period
              is capped at the greater of (a) amounts you paid Sentience in that period or (b) $100.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">13) Indemnity</h2>
            <p>
              You’ll defend and indemnify Sentience against claims arising from your use of the Service, your User Content,
              or your breach of these Terms.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">14) Termination</h2>
            <p>
              We may suspend or terminate your access at any time. You may stop using the Service at any time.
              Sections that naturally survive (e.g., ownership, disclaimers, limitations, disputes) will remain in effect.
            </p>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">15) Governing Law &amp; Disputes</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Law:</strong> New York law (without regard to conflict-of-laws rules).</li>
              <li>
                <strong>Arbitration:</strong> Any dispute will be resolved by binding, individual arbitration administered by AAA under its rules.
                No class actions or jury trials. Judgment on the award may be entered in any court with jurisdiction.
              </li>
              <li>
                <strong>Opt-Out:</strong> You may opt out of arbitration by emailing <a href="mailto:legal@sentience.company" className="text-blue-300 hover:text-blue-200">legal@sentience.company</a> within 30 days of first agreeing to these Terms.
                Include your name and the account email.
              </li>
              <li>
                Either party may seek injunctive relief in court to protect IP or confidential information.
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">16) Miscellaneous</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>These Terms are the entire agreement between you and Sentience.</li>
              <li>If any provision is unenforceable, the rest remains in effect.</li>
              <li>We may assign these Terms; you may not without our written consent.</li>
              <li>Failure to enforce a provision isn’t a waiver.</li>
              <li>&quot;Including&quot; means &quot;including without limitation.&quot;</li>
            </ul>

            <h2 className="text-2xl font-bold text-white mt-8 mb-2">17) Contact</h2>
            <p>
              Questions? Email <a href="mailto:support@sentience.company" className="text-blue-300 hover:text-blue-200">support@sentience.company</a>.
            </p>
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-0 right-0">
        <Footer />
      </div>
    </div>
  );
}